import { ArrowClockwise, Check, Lock, WarningOctagon } from '@phosphor-icons/react';
import type { Side, SlateGameDto } from '@pickem/shared';
import type { PickState } from '../api/usePicks';
import { cx, LevelTag, LiveStatus, ResultPill, TeamMark } from './ui';

/**
 * The pick card — the visual centrepiece of the whole application.
 *
 * Two enormous tap targets split by a hairline. One tap picks a side. The design holds to
 * a few rules the brief is explicit about:
 *
 *  - The line is always shown from the side you would be taking, in monospace, at a size
 *    you can read without looking twice.
 *  - The selected state is unmistakable: accent border, tinted fill, and a filled check.
 *    You should be able to tell at arm's length which side you are on.
 *  - Save state is *per game* and never lies. A failure rolls the selection back and says
 *    so, with a retry right there rather than a toast that has already gone.
 */

interface PickCardProps {
  game: SlateGameDto;
  index: number;
  countdown: string;
  pickState: PickState;
  onPick: (slateGameId: string, selection: Side) => void;
  onRetry: (slateGameId: string) => void;
}

export function PickCard({ game, index, countdown, pickState, onPick, onRetry }: PickCardProps) {
  const selected = game.myPick?.selection ?? null;
  const isVoid = game.status === 'voided';
  const settled = game.result !== 'pending' && game.result !== 'void';
  const live = game.live.phase === 'in' || game.live.phase === 'halftime';

  return (
    <article
      className="taste-rise overflow-hidden rounded-card border border-line bg-field-850"
      style={{ '--index': index } as React.CSSProperties}
    >
      {/* Meta strip -------------------------------------------------------- */}
      <div className="flex items-center gap-2.5 border-b border-line px-3.5 py-2.5">
        <span className="grid size-5 shrink-0 place-items-center rounded bg-field-800 font-mono text-[10px] font-semibold text-field-400">
          {game.slot}
        </span>
        <LevelTag level={game.level} />
        <span className="truncate font-mono text-[11px] text-field-500">
          {formatKickoff(game.kickoffAt)}
          {game.network ? ` · ${game.network}` : ''}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {live && <LiveStatus live={game.live} />}
          {!live && !game.locked && (
            <span className="font-mono text-[11px] font-medium tabular-nums text-signal">
              {countdown}
            </span>
          )}
          {!live && game.locked && !isVoid && (
            <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] text-field-500">
              <Lock size={11} weight="fill" />
              Locked
            </span>
          )}
          {isVoid && <ResultPill result="void" compact />}
        </div>
      </div>

      {/* The two sides ----------------------------------------------------- */}
      <div className="relative grid grid-cols-2">
        <SideButton
          game={game}
          side="away"
          selected={selected === 'away'}
          disabled={game.locked}
          onPick={onPick}
        />

        {/* Hairline divider carrying the AT/VS relationship. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-3 left-1/2 flex w-px -translate-x-1/2 items-center justify-center bg-field-800"
        >
          <span className="rounded bg-field-850 px-1 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-field-600">
            {game.neutralSite ? 'vs' : 'at'}
          </span>
        </div>

        <SideButton
          game={game}
          side="home"
          selected={selected === 'home'}
          disabled={game.locked}
          onPick={onPick}
        />
      </div>

      {/* Status foot ------------------------------------------------------- */}
      <StatusFoot
        game={game}
        pickState={pickState}
        settled={settled}
        isVoid={isVoid}
        onRetry={onRetry}
      />
    </article>
  );
}

function SideButton({
  game,
  side,
  selected,
  disabled,
  onPick,
}: {
  game: SlateGameDto;
  side: Side;
  selected: boolean;
  disabled: boolean;
  onPick: (slateGameId: string, selection: Side) => void;
}) {
  const data = side === 'away' ? game.away : game.home;
  const settled = game.result !== 'pending' && game.result !== 'void';
  // `data.score` is the frozen final written at grading time; `live` is the running
  // scoreboard. Preferring the frozen value once it exists keeps a graded game stable even
  // if the feed later disagrees with itself.
  const liveScore = data.score ?? (side === 'away' ? game.live.awayScore : game.live.homeScore);
  // Once a game is settled, the side that missed recedes so the eye lands on the outcome.
  const faded = settled && data.covered === false;

  return (
    <button
      onClick={() => !disabled && onPick(game.id, side)}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={`${data.team.name} ${data.lineLabel}`}
      className={cx(
        // Tall enough to be a comfortable thumb target with room to spare.
        'relative flex min-h-[104px] flex-col items-center justify-center gap-2 px-3 py-4',
        'transition-all duration-300 ease-[var(--ease-out-expo)]',
        !disabled && 'active:scale-[0.97]',
        selected
          ? 'bg-signal/[0.12] inset-ring-2 inset-ring-signal/70'
          : disabled
            ? 'cursor-default'
            : 'hover:bg-field-800/70',
        faded && 'opacity-45',
      )}
    >
      {selected && (
        <span className="taste-pop absolute right-2.5 top-2.5 grid size-5 place-items-center rounded-full bg-signal text-ink">
          <Check size={12} weight="bold" />
        </span>
      )}

      <TeamMark team={data.team} size={38} dim={faded} />

      <div className="flex w-full flex-col items-center gap-0.5">
        <span
          className={cx(
            'w-full truncate text-center text-[13px] font-medium leading-tight',
            selected ? 'text-field-100' : 'text-field-200',
          )}
        >
          {data.team.shortName}
        </span>

        <span
          className={cx(
            'font-mono text-xl font-semibold tabular-nums leading-none',
            selected ? 'text-signal' : data.isFavorite ? 'text-field-100' : 'text-field-300',
          )}
        >
          {data.lineLabel}
        </span>
      </div>

      {liveScore !== null && (
        <span
          className={cx(
            'font-mono text-2xl font-semibold tabular-nums leading-none',
            data.covered === true ? 'text-win' : 'text-field-300',
          )}
        >
          {liveScore}
        </span>
      )}
    </button>
  );
}

/**
 * The honesty layer. This is the part that makes the difference between "I think it saved"
 * and "I know it saved".
 */
function StatusFoot({
  game,
  pickState,
  settled,
  isVoid,
  onRetry,
}: {
  game: SlateGameDto;
  pickState: PickState;
  settled: boolean;
  isVoid: boolean;
  onRetry: (slateGameId: string) => void;
}) {
  if (pickState.status === 'error') {
    return (
      <div className="flex items-center gap-2 border-t border-loss/25 bg-loss/[0.07] px-3.5 py-2.5">
        <WarningOctagon size={15} weight="fill" className="shrink-0 text-loss" />
        <span className="min-w-0 flex-1 truncate text-[12px] text-loss">{pickState.error}</span>
        {!pickState.locked && (
          <button
            onClick={() => onRetry(game.id)}
            className="flex shrink-0 items-center gap-1 rounded-md border border-loss/40 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-loss transition-colors hover:bg-loss/10 active:scale-95"
          >
            <ArrowClockwise size={11} weight="bold" />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (pickState.status === 'saving') {
    return (
      <Foot>
        <ArrowClockwise size={13} weight="bold" className="animate-spin text-field-400" />
        <span className="text-field-400">Saving</span>
      </Foot>
    );
  }

  if (pickState.status === 'saved') {
    return (
      <div className="taste-pop flex items-center gap-1.5 border-t border-win/25 bg-win/[0.07] px-3.5 py-2 text-[12px] text-win">
        <Check size={13} weight="bold" />
        <span className="font-medium">Saved</span>
      </div>
    );
  }

  if (isVoid) {
    return (
      <Foot>
        <span className="text-field-500">
          {game.voidReason ?? 'Pulled by the commissioner. This game scores nothing.'}
        </span>
      </Foot>
    );
  }

  if (settled && game.myPick) {
    return (
      <div className="flex items-center gap-2 border-t border-line px-3.5 py-2">
        <ResultPill result={game.myPick.result} compact />
        <span className="font-mono text-[11px] text-field-500">
          {game.myPick.pointsAwarded != null
            ? `+${formatPoints(game.myPick.pointsAwarded)} pt${game.myPick.pointsAwarded === 1 ? '' : 's'}`
            : ''}
        </span>
        {game.revealed && game.otherPicks.length > 0 && (
          <span className="ml-auto truncate font-mono text-[11px] text-field-500">
            {sideCount(game)}
          </span>
        )}
      </div>
    );
  }

  if (game.locked && !game.myPick) {
    return (
      <Foot>
        <span className="text-field-500">No pick made — this one scores zero.</span>
      </Foot>
    );
  }

  // Open game: show participation without leaking anybody's side.
  return (
    <Foot>
      <span className="font-mono text-field-500">
        {game.pickCount} of {game.memberCount} picked
      </span>
      {game.myPick && (
        <span className="ml-auto font-mono text-field-600">
          Locked in · {game.myPick.selection === 'away' ? game.away.team.abbreviation : game.home.team.abbreviation}
        </span>
      )}
    </Foot>
  );
}

function Foot({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-t border-line px-3.5 py-2 text-[12px]">
      {children}
    </div>
  );
}

function sideCount(game: SlateGameDto): string {
  const all = [...game.otherPicks.map((p) => p.selection), ...(game.myPick ? [game.myPick.selection] : [])];
  const away = all.filter((s) => s === 'away').length;
  const home = all.length - away;
  return `${away} ${game.away.team.abbreviation} · ${home} ${game.home.team.abbreviation}`;
}

function formatPoints(points: number): string {
  return points % 1 === 0 ? String(points) : points.toFixed(1);
}

/** Kickoff in the reader's own timezone — the browser resolves it, not the server. */
export function formatKickoff(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
